import {createApi} from '@reduxjs/toolkit/query/react';
import {buildVocabularyQueryItem, vocabResponseKey} from '@hooks/vocabularyQuery';
import {handleError, handleSuccess} from '@store/app.slice';
import {baseQuery} from '@store/baseQuery';
import {ProfileApi} from '../Profile/Profile.api';
import type {BrandOption, SaveMyTopPayload} from './types';

type VocabRow = {
    id?: number;
    label?: string;
    value?: string | null;
};

type VocabPage = {
    data?: VocabRow[];
};

const toBrandOption = (row: VocabRow): BrandOption | null => {
    if (typeof row.id !== 'number') {
        return null;
    }

    const value = row.value == null ? '' : String(row.value).trim();
    const label = (row.label ?? '').trim() || value;
    if (!label) {
        return null;
    }

    return {
        id: row.id,
        label,
        value: value || label,
    };
};

export const MyTopApi = createApi({
    reducerPath: 'myTopApi',
    baseQuery: baseQuery(),
    endpoints: (build) => ({
        getProductTypeBrands: build.query<BrandOption[], {vocabulary: string}>({
            query: ({vocabulary}) => ({
                url: '/vocabulary',
                method: 'POST',
                body: {
                    vocabularies: [{...buildVocabularyQueryItem(vocabulary), per_page: 1000}],
                },
            }),
            transformResponse: (response: Record<string, VocabPage>, _meta, arg) => {
                const key = vocabResponseKey(arg.vocabulary);
                const rows = response[key]?.data ?? [];

                return rows.flatMap((row) => {
                    const brand = toBrandOption(row);
                    return brand ? [brand] : [];
                });
            },
        }),
        saveMyTop: build.mutation<{message?: string; my_top: SaveMyTopPayload}, SaveMyTopPayload>({
            query: (body) => ({
                url: '/user/my-top',
                method: 'POST',
                body,
            }),
            async onQueryStarted(_arg, {dispatch, getState, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(handleSuccess({message: 'Набор сохранён'}));
                    const token = (getState() as unknown as {app: {token: unknown}}).app.token;
                    dispatch(
                        ProfileApi.util.updateQueryData(
                            'getUserProfile',
                            {token},
                            (draft) => {
                                if (draft) {
                                    draft.my_top = data.my_top;
                                }
                            },
                        ),
                    );
                } catch (e: any) {
                    dispatch(handleError({
                        message: e?.error?.data?.message || 'Не удалось сохранить набор',
                        errors: {},
                    }));
                }
            },
        }),
    }),
});

export const {
    useGetProductTypeBrandsQuery,
    useSaveMyTopMutation,
} = MyTopApi;
